import React, { useState, useEffect, useRef, useMemo } from 'react'
import { X, Loader2, Save, ClipboardPaste, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react'
import { useProaStore } from '@/stores/proa-store'
import {
  useBulkUpsertMetricProas,
  useGetMetricProas,
  useUpsertMetricProaBreakdown,
  useRemoveBreakdown,
  type MonthlyDistributionType,
  type EntryMode,
  type MetricProa,
  type MetricBreakdown,
} from '../hooks/useProaData'
import { trpc } from '@/api/trpc'

interface DREBudgetModalProps {
  isOpen: boolean
  onClose: () => void
}

// Standard DRE line items for budget planning (must match backend enum)
const DRE_LINE_ITEMS = [
  { id: 'ReceitaBruta', name: 'Receita Bruta', isRevenue: true, supportsBreakdown: true },
  { id: 'Repasse', name: '(-) Repasse', isRevenue: false, supportsBreakdown: true },
  { id: 'PIS', name: '(-) PIS', isRevenue: false, supportsBreakdown: false },
  { id: 'COFINS', name: '(-) COFINS', isRevenue: false, supportsBreakdown: false },
  { id: 'ISS', name: '(-) ISS', isRevenue: false, supportsBreakdown: false },
  { id: 'IRPJ', name: '(-) IRPJ', isRevenue: false, supportsBreakdown: false },
  { id: 'ContribuicaoSocial', name: '(-) Contribuição Social', isRevenue: false, supportsBreakdown: false },
  { id: 'ServicosPrestados', name: '(-) Serviços Prestados', isRevenue: false, supportsBreakdown: true },
  { id: 'Timesheet', name: '(-) Timesheet', isRevenue: false, supportsBreakdown: true },
  { id: 'Folha', name: '(-) Folha de Pagamento', isRevenue: false, supportsBreakdown: false },
  { id: 'MaoDeObra', name: '(-) Mão de Obra', isRevenue: false, supportsBreakdown: false },
  { id: 'Bonificacao', name: '(-) Bonificação', isRevenue: false, supportsBreakdown: false },
  { id: 'DespesasOperacionais', name: '(-) Despesas Operacionais', isRevenue: false, supportsBreakdown: false },
  { id: 'ReceitasFinanceiras', name: '(+) Receitas Financeiras', isRevenue: true, supportsBreakdown: false },
  { id: 'DespesasFinanceiras', name: '(-) Despesas Financeiras', isRevenue: false, supportsBreakdown: false },
  { id: 'LucroLiquido', name: '(=) Lucro Líquido', isRevenue: true, supportsBreakdown: false },
]

const DISTRIBUTION_OPTIONS: { value: MonthlyDistributionType; label: string }[] = [
  { value: 'even', label: 'Uniforme (1/12)' },
  { value: 'weighted', label: 'Ponderado' },
  { value: 'front_loaded', label: 'Front Loaded' },
  { value: 'back_loaded', label: 'Back Loaded' },
]

// Mapping from pasted text labels to backend line item IDs
const PASTE_LABEL_MAPPING: Record<string, string> = {
  // Revenue
  receita: 'ReceitaBruta',
  'receita bruta': 'ReceitaBruta',
  'receita parcerias': 'Repasse',
  repasse: 'Repasse',
  // Costs
  'custo dos serviços prestados': 'ServicosPrestados',
  'custo dos servicos prestados': 'ServicosPrestados',
  'serviços prestados': 'ServicosPrestados',
  'servicos prestados': 'ServicosPrestados',
  timesheet: 'Timesheet',
  'folha e mão de obra': 'Folha',
  'folha e mao de obra': 'Folha',
  'folha de pagamento': 'Folha',
  folha: 'Folha',
  'mão de obra': 'MaoDeObra',
  'mao de obra': 'MaoDeObra',
  bonificação: 'Bonificacao',
  bonificacao: 'Bonificacao',
  'despesas administrativas': 'DespesasOperacionais',
  'despesas operacionais': 'DespesasOperacionais',
  // Financial
  'receitas financeiras': 'ReceitasFinanceiras',
  'despesas financeiras': 'DespesasFinanceiras',
  // Taxes
  'imposto de renda pessoa jurídica': 'IRPJ',
  'imposto de renda pessoa juridica': 'IRPJ',
  irpj: 'IRPJ',
  'contribuição social': 'ContribuicaoSocial',
  'contribuicao social': 'ContribuicaoSocial',
  pis: 'PIS',
  cofins: 'COFINS',
  iss: 'ISS',
  // Result
  'lucro liquido': 'LucroLiquido',
  'lucro líquido': 'LucroLiquido',
}

interface LocalBreakdown {
  dimensionId: string
  dimensionLabel: string
  annual: number
  distribution: MonthlyDistributionType
}

interface LocalMetricEntry {
  lineItem: string
  entryMode: EntryMode
  annual: number
  distribution: MonthlyDistributionType
  breakdowns: LocalBreakdown[]
  ajuste: number
  expanded: boolean
}

function parsePastedBudget(text: string): Record<string, number> {
  const result: Record<string, number> = {}
  const lines = text.trim().split('\n')

  for (const line of lines) {
    // Match: optional prefix like (-) or (+), then label, then spaces/tabs, then number
    // Handles both "(-) Label    123,456.78" and "(-)Label    123,456.78"
    const match = line.match(/^[\(\-\+\)]*\s*(.+?)\s{2,}([\d.,]+)\s*$/)
    if (!match) {
      // Try tab-separated format
      const tabMatch = line.match(/^[\(\-\+\)]*\s*(.+?)\t+([\d.,]+)\s*$/)
      if (tabMatch) {
        const label = tabMatch[1].trim().replace(/^[\(\)\-\+\s]+/, '').replace(/[\(\)\-\+\s]+$/, '').toLowerCase()
        const lineItemId = PASTE_LABEL_MAPPING[label]
        if (lineItemId) {
          const value = parseCurrencyToNumber(tabMatch[2])
          if (value > 0) {
            result[lineItemId] = value
            console.log(`[Paste] Mapped "${label}" -> ${lineItemId} = ${value}`)
          }
        } else {
          console.log(`[Paste] Unrecognized label: "${label}"`)
        }
      }
      continue
    }

    let label = match[1].trim()
    const valueStr = match[2]

    // Clean up the label: remove leading/trailing parentheses, dashes, plus signs
    label = label.replace(/^[\(\)\-\+\s]+/, '').replace(/[\(\)\-\+\s]+$/, '').toLowerCase()

    const lineItemId = PASTE_LABEL_MAPPING[label]
    if (!lineItemId) {
      console.log(`[Paste] Unrecognized label: "${label}"`)
      continue
    }

    const value = parseCurrencyToNumber(valueStr)
    if (value > 0) {
      result[lineItemId] = value
      console.log(`[Paste] Mapped "${label}" -> ${lineItemId} = ${value}`)
    }
  }

  return result
}

function parseCurrencyToNumber(value: string): number {
  if (!value) return 0

  let cleaned = value.replace(/[R$\s]/g, '').trim()

  const hasComma = cleaned.includes(',')
  const hasDot = cleaned.includes('.')

  if (hasComma && hasDot) {
    // Determine format by position: which one comes last?
    const lastCommaPos = cleaned.lastIndexOf(',')
    const lastDotPos = cleaned.lastIndexOf('.')

    if (lastDotPos > lastCommaPos) {
      // US format: 86,797,737.76 - comma is thousands, dot is decimal
      cleaned = cleaned.replace(/,/g, '')
    } else {
      // Brazilian format: 86.797.737,76 - dot is thousands, comma is decimal
      cleaned = cleaned.replace(/\./g, '').replace(',', '.')
    }
  } else if (hasComma && !hasDot) {
    // Could be Brazilian decimal (1.234,56) or thousands (1,234)
    // Check if comma has exactly 2 digits after it (likely decimal)
    const afterComma = cleaned.split(',')[1]
    if (afterComma && afterComma.length === 2) {
      cleaned = cleaned.replace(',', '.')
    } else {
      // Likely thousands separator, remove it
      cleaned = cleaned.replace(/,/g, '')
    }
  } else if (hasDot) {
    const parts = cleaned.split('.')
    const lastPart = parts[parts.length - 1]
    if (parts.length > 1 && lastPart.length === 2) {
      // Keep as is - it's a decimal
    } else {
      // Likely thousands separator, remove it
      cleaned = cleaned.replace(/\./g, '')
    }
  }

  return parseFloat(cleaned) || 0
}

function formatCurrencyDisplay(value: number, allowZero = false): string {
  if (value === 0 && !allowZero) return ''
  if (value === null || value === undefined || isNaN(value)) return ''

  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Parse pasted client breakdown data
 * Expected format: "CLIENT NAME    VALUE" (one per line)
 * Examples:
 *   BB CORRETORA DE SEGU    23.700.000,00
 *   PETROBRAS               48.533.000,00
 */
function parsePastedClientBreakdowns(text: string): LocalBreakdown[] {
  const result: LocalBreakdown[] = []
  const lines = text.trim().split('\n')

  for (const line of lines) {
    if (!line.trim()) continue

    // Match: client name (letters, spaces, dashes, dots) followed by spaces and then a number
    // The regex captures everything before the number as the client name
    const match = line.match(/^(.+?)\s{2,}([\d.,]+)\s*$/)
    if (!match) {
      // Try alternative format: tab-separated
      const tabMatch = line.match(/^(.+?)\t+([\d.,]+)\s*$/)
      if (tabMatch) {
        const clientName = tabMatch[1].trim()
        const value = parseCurrencyToNumber(tabMatch[2])
        if (clientName && value > 0) {
          result.push({
            dimensionId: clientName, // Will be matched to real client ID later
            dimensionLabel: clientName,
            annual: value,
            distribution: 'even',
          })
        }
      }
      continue
    }

    const clientName = match[1].trim()
    const value = parseCurrencyToNumber(match[2])

    if (clientName && value > 0) {
      result.push({
        dimensionId: clientName, // Will be matched to real client ID later
        dimensionLabel: clientName,
        annual: value,
        distribution: 'even',
      })
      console.log(`[Paste Clients] Parsed: "${clientName}" = ${value}`)
    }
  }

  return result
}

export function DREBudgetModal({ isOpen, onClose }: DREBudgetModalProps) {
  const { selectedScenarioId, selectedYear } = useProaStore()

  // Fetch existing metric PROAs for this scenario
  const { data: existingProas } = useGetMetricProas(selectedScenarioId, selectedYear)

  // Fetch clients from client matrix (full year data for selected year)
  const { data: clientMatrixData } = trpc.dre.getClientMatrix.useQuery(
    {
      dataInicial: new Date(`${selectedYear}-01-01`),
      dataFinal: new Date(`${selectedYear}-12-31`),
    },
    {
      enabled: isOpen && !!selectedYear,
      staleTime: 10 * 60 * 1000, // 10 minutes cache
    }
  )

  // Transform client matrix data to the format we need for autocomplete
  const availableClients = useMemo(() => {
    if (!clientMatrixData?.clients) return []
    return clientMatrixData.clients.map((c) => ({
      codigo: c.codigoCliente ?? '',
      nome: c.nomeCliente ?? '',
    }))
  }, [clientMatrixData])

  // Mutations
  const bulkUpsert = useBulkUpsertMetricProas()
  const upsertBreakdown = useUpsertMetricProaBreakdown()
  const removeBreakdownMutation = useRemoveBreakdown()

  // Form state
  const [metrics, setMetrics] = useState<Record<string, LocalMetricEntry>>({})
  const [showPasteArea, setShowPasteArea] = useState(false)
  const [pasteText, setPasteText] = useState('')
  const [savingLineItem, setSavingLineItem] = useState<string | null>(null)
  const pasteTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Client breakdown paste state
  const [showClientPasteArea, setShowClientPasteArea] = useState<string | null>(null) // lineItem or null
  const [clientPasteText, setClientPasteText] = useState('')
  const clientPasteTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<string[]>([])

  // Initialize form with existing data
  useEffect(() => {
    const initial: Record<string, LocalMetricEntry> = {}

    DRE_LINE_ITEMS.forEach((item) => {
      const existing = existingProas?.find((p: MetricProa) => p.lineItem === item.id)

      if (existing) {
        initial[item.id] = {
          lineItem: item.id,
          entryMode: item.supportsBreakdown ? 'breakdown' : existing.entryMode,
          annual: existing.annual,
          distribution: existing.distribution.type,
          breakdowns: (existing.breakdowns || []).map((b: MetricBreakdown) => ({
            dimensionId: b.dimensionId,
            dimensionLabel: b.dimensionLabel,
            annual: b.annual,
            distribution: b.distribution.type,
          })),
          ajuste: existing.ajuste || 0,
          expanded: item.supportsBreakdown,
        }
      } else {
        initial[item.id] = {
          lineItem: item.id,
          entryMode: item.supportsBreakdown ? 'breakdown' : 'direct',
          annual: 0,
          distribution: 'even',
          breakdowns: [],
          ajuste: 0,
          expanded: item.supportsBreakdown,
        }
      }
    })

    setMetrics(initial)
  }, [existingProas])

  const toggleExpanded = (lineItem: string) => {
    setMetrics((prev) => ({
      ...prev,
      [lineItem]: {
        ...prev[lineItem],
        expanded: !prev[lineItem].expanded,
      },
    }))
  }

  const toggleEntryMode = (lineItem: string) => {
    setMetrics((prev) => {
      const current = prev[lineItem]
      const newMode: EntryMode = current.entryMode === 'direct' ? 'breakdown' : 'direct'

      if (newMode === 'breakdown') {
        // Switching to breakdown mode - expand and start with empty breakdowns
        return {
          ...prev,
          [lineItem]: {
            ...current,
            entryMode: 'breakdown',
            expanded: true,
            // Keep existing annual as starting point, will be recalculated from breakdowns
          },
        }
      } else {
        // Switching back to direct - collapse and clear breakdowns
        const breakdownSum = current.breakdowns.reduce((sum, b) => sum + b.annual, 0)
        return {
          ...prev,
          [lineItem]: {
            ...current,
            entryMode: 'direct',
            expanded: false,
            annual: breakdownSum + current.ajuste || current.annual,
            breakdowns: [],
            ajuste: 0,
          },
        }
      }
    })
  }

  const handleDirectValueChange = (lineItem: string, value: string) => {
    const numericValue = parseCurrencyToNumber(value)
    setMetrics((prev) => ({
      ...prev,
      [lineItem]: {
        ...prev[lineItem],
        annual: numericValue,
      },
    }))
  }

  const handleDistributionChange = (lineItem: string, distribution: MonthlyDistributionType) => {
    setMetrics((prev) => ({
      ...prev,
      [lineItem]: {
        ...prev[lineItem],
        distribution,
      },
    }))
  }

  // Sync Repasse breakdowns to match ReceitaBruta clients
  const syncRepasseWithReceita = (updated: Record<string, LocalMetricEntry>): Record<string, LocalMetricEntry> => {
    const receita = updated['ReceitaBruta']
    const repasse = updated['Repasse']
    if (!receita || !repasse) return updated

    // Keep existing Repasse values for matching clients, add missing ones with 0
    const newRepasseBreakdowns = receita.breakdowns.map((rb) => {
      const existing = repasse.breakdowns.find((b) => b.dimensionId === rb.dimensionId)
      return existing || {
        dimensionId: rb.dimensionId,
        dimensionLabel: rb.dimensionLabel,
        annual: 0,
        distribution: 'even' as MonthlyDistributionType,
      }
    })

    const repasseSum = newRepasseBreakdowns.reduce((sum, b) => sum + b.annual, 0)

    return {
      ...updated,
      Repasse: {
        ...repasse,
        breakdowns: newRepasseBreakdowns,
        annual: repasseSum + repasse.ajuste,
      },
    }
  }

  const addBreakdown = (lineItem: string) => {
    setMetrics((prev) => {
      const current = prev[lineItem]
      const updated = {
        ...prev,
        [lineItem]: {
          ...current,
          breakdowns: [
            ...current.breakdowns,
            {
              dimensionId: '',
              dimensionLabel: '',
              annual: 0,
              distribution: 'even' as MonthlyDistributionType,
            },
          ],
        },
      }
      return lineItem === 'ReceitaBruta' ? syncRepasseWithReceita(updated) : updated
    })
  }

  const updateBreakdown = (
    lineItem: string,
    index: number,
    field: keyof LocalBreakdown,
    value: string | number
  ) => {
    setMetrics((prev) => {
      const current = prev[lineItem]
      const newBreakdowns = [...current.breakdowns]

      if (field === 'dimensionId' && typeof value === 'string') {
        // When selecting a client, also set the label
        const selectedClient = availableClients.find((c) => c.codigo === value)
        newBreakdowns[index] = {
          ...newBreakdowns[index],
          dimensionId: value,
          dimensionLabel: selectedClient?.nome || newBreakdowns[index].dimensionLabel,
        }
      } else {
        newBreakdowns[index] = {
          ...newBreakdowns[index],
          [field]: value,
        }
      }

      // Recalculate annual from breakdowns
      const breakdownSum = newBreakdowns.reduce((sum, b) => sum + b.annual, 0)

      const updated = {
        ...prev,
        [lineItem]: {
          ...current,
          breakdowns: newBreakdowns,
          annual: breakdownSum + current.ajuste,
        },
      }
      return lineItem === 'ReceitaBruta' ? syncRepasseWithReceita(updated) : updated
    })
  }

  const removeBreakdown = (lineItem: string, index: number) => {
    setMetrics((prev) => {
      const current = prev[lineItem]
      const newBreakdowns = current.breakdowns.filter((_, i) => i !== index)
      const breakdownSum = newBreakdowns.reduce((sum, b) => sum + b.annual, 0)

      const updated = {
        ...prev,
        [lineItem]: {
          ...current,
          breakdowns: newBreakdowns,
          annual: breakdownSum + current.ajuste,
          entryMode: newBreakdowns.length === 0 ? 'direct' : current.entryMode,
        },
      }
      return lineItem === 'ReceitaBruta' ? syncRepasseWithReceita(updated) : updated
    })
  }

  const handleAjusteChange = (lineItem: string, value: string) => {
    const numericValue = parseCurrencyToNumber(value)
    setMetrics((prev) => {
      const current = prev[lineItem]
      const breakdownSum = current.breakdowns.reduce((sum, b) => sum + b.annual, 0)

      return {
        ...prev,
        [lineItem]: {
          ...current,
          ajuste: numericValue,
          annual: breakdownSum + numericValue,
        },
      }
    })
  }

  const handlePasteApply = () => {
    if (!pasteText.trim()) return

    const parsed = parsePastedBudget(pasteText)

    setMetrics((prev) => {
      const updated = { ...prev }
      for (const [lineItemId, value] of Object.entries(parsed)) {
        if (updated[lineItemId]) {
          updated[lineItemId] = {
            ...updated[lineItemId],
            annual: value,
            entryMode: 'direct', // Pasted values are direct
            breakdowns: [],
            ajuste: 0,
          }
        }
      }
      return updated
    })

    setShowPasteArea(false)
    setPasteText('')
  }

  const handleClientPasteApply = (lineItem: string) => {
    if (!clientPasteText.trim()) return

    const parsed = parsePastedClientBreakdowns(clientPasteText)
    if (parsed.length === 0) return

    // Try to match pasted client names to available clients
    const matchedBreakdowns = parsed.map((breakdown) => {
      // Try to find a matching client by name (case-insensitive, partial match)
      const lowerLabel = breakdown.dimensionLabel.toLowerCase()
      const matchingClient = availableClients.find((c) => {
        const lowerNome = c.nome.toLowerCase()
        return lowerNome === lowerLabel || lowerNome.includes(lowerLabel) || lowerLabel.includes(lowerNome)
      })

      if (matchingClient) {
        return {
          ...breakdown,
          dimensionId: matchingClient.codigo,
          dimensionLabel: matchingClient.nome,
        }
      }
      return breakdown
    })

    setMetrics((prev) => {
      const current = prev[lineItem]
      const newBreakdowns = [...current.breakdowns, ...matchedBreakdowns]
      const breakdownSum = newBreakdowns.reduce((sum, b) => sum + b.annual, 0)

      const updated: Record<string, LocalMetricEntry> = {
        ...prev,
        [lineItem]: {
          ...current,
          entryMode: 'breakdown' as EntryMode,
          breakdowns: newBreakdowns,
          annual: breakdownSum + current.ajuste,
          expanded: true,
        },
      }
      return lineItem === 'ReceitaBruta' ? syncRepasseWithReceita(updated) : updated
    })

    setShowClientPasteArea(null)
    setClientPasteText('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setValidationErrors([])

    if (!selectedScenarioId) return

    // Build a set of valid client codes for validation
    const validClientCodes = new Set(availableClients.map((c) => c.codigo))

    // Separate direct entries from breakdown entries
    const directItems = Object.values(metrics)
      .filter((m) => m.entryMode === 'direct' && m.annual !== 0)
      .map((m) => ({
        lineItem: m.lineItem,
        annual: m.annual,
        distribution: { type: m.distribution },
      }))

    const breakdownItems = Object.values(metrics).filter(
      (m) => m.entryMode === 'breakdown' && m.breakdowns.length > 0
    )

    // Validate breakdown items - ensure all have valid client codes
    const errors: string[] = []
    for (const metric of breakdownItems) {
      const lineItemName = DRE_LINE_ITEMS.find((item) => item.id === metric.lineItem)?.name || metric.lineItem
      for (const breakdown of metric.breakdowns) {
        if (!breakdown.dimensionId || !validClientCodes.has(breakdown.dimensionId)) {
          errors.push(`${lineItemName}: Cliente "${breakdown.dimensionLabel}" não encontrado no sistema`)
        }
      }
    }

    if (errors.length > 0) {
      setValidationErrors(errors)
      return
    }

    try {
      // Save direct entries in bulk
      if (directItems.length > 0) {
        await bulkUpsert.mutateAsync({
          scenarioId: selectedScenarioId,
          year: selectedYear,
          items: directItems,
        })
      }

      // Save breakdown entries one by one (they need special handling)
      for (const metric of breakdownItems) {
        setSavingLineItem(metric.lineItem)
        await upsertBreakdown.mutateAsync({
          scenarioId: selectedScenarioId,
          year: selectedYear,
          lineItem: metric.lineItem,
          breakdowns: metric.breakdowns
            .filter((b) => b.dimensionId && b.annual > 0)
            .map((b) => ({
              dimensionId: b.dimensionId,
              dimensionLabel: b.dimensionLabel,
              annual: b.annual,
              distribution: { type: b.distribution },
            })),
          ajuste: metric.ajuste,
          distribution: { type: metric.distribution },
        })
      }

      onClose()
    } catch (error) {
      console.error('Failed to save DRE budgets:', error)
    } finally {
      setSavingLineItem(null)
    }
  }

  const isSaving = bulkUpsert.isPending || upsertBreakdown.isPending
  const saveError = bulkUpsert.error || upsertBreakdown.error

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-[#1E2024] rounded-xl border border-[#2A2D34] w-full max-w-4xl mx-4 shadow-xl max-h-[90vh] flex flex-col font-body">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#2A2D34]">
          <div>
            <h2 className="text-lg font-semibold text-white">Metas DRE - {selectedYear}</h2>
            <p className="text-sm text-[#838998]">
              Defina valores anuais diretos ou por cliente (breakdown)
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-[#2A2D34] transition-colors"
          >
            <X className="h-5 w-5 text-[#838998]" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto p-4">
            {/* Paste from Spreadsheet Section */}
            <div className="mb-4">
              <button
                type="button"
                onClick={() => {
                  setShowPasteArea(!showPasteArea)
                  if (!showPasteArea) {
                    setTimeout(() => pasteTextareaRef.current?.focus(), 100)
                  }
                }}
                className="px-3 py-2 text-sm font-medium bg-[#24262B] text-[#838998] rounded-lg hover:bg-[#2A2D34] hover:text-white transition-colors flex items-center gap-2 border border-[#2A2D34]"
              >
                <ClipboardPaste className="h-4 w-4" />
                Colar da Planilha (Valores Diretos)
              </button>

              {showPasteArea && (
                <div className="mt-3 p-3 rounded-lg bg-[#24262B] border border-[#2A2D34]">
                  <p className="text-xs text-[#838998] mb-2">
                    Cole os dados no formato: "Descrição R$ valor" (um item por linha)
                  </p>
                  <textarea
                    ref={pasteTextareaRef}
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    placeholder={`RECEITA    R$ 127.202.506,45
(-) Custo dos Serviços Prestados    R$ 63.131.472,70
(-) Folha e Mão de Obra    R$ 18.866.265,06
...`}
                    rows={6}
                    className="w-full rounded-md border border-[#2A2D34] bg-[#1E2024] px-3 py-2 text-sm text-white placeholder:text-[#5a5f6b] focus:outline-none focus:ring-2 focus:ring-[#D2FA52] focus:ring-offset-1 focus:ring-offset-[#1E2024] font-body resize-none"
                  />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasteArea(false)
                        setPasteText('')
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-[#838998] hover:text-white transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handlePasteApply}
                      disabled={!pasteText.trim()}
                      className="px-3 py-1.5 text-xs font-medium bg-[#D2FA52] text-[#0D0E10] rounded-md hover:bg-[#c2ea42] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Metrics Table */}
            <div className="space-y-1">
              {DRE_LINE_ITEMS.map((item) => {
                const metric = metrics[item.id]
                if (!metric) return null

                const isBreakdownMode = item.supportsBreakdown ? true : metric.entryMode === 'breakdown'
                const breakdownSum = metric.breakdowns.reduce((sum, b) => sum + b.annual, 0)

                return (
                  <div
                    key={item.id}
                    className="border border-[#2A2D34] rounded-lg overflow-hidden"
                  >
                    {/* Main Row */}
                    <div className="flex items-center gap-2 p-3 bg-[#1E2024]">
                      {/* Expand/Collapse for breakdown mode */}
                      {item.supportsBreakdown && isBreakdownMode ? (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(item.id)}
                          className="p-1 rounded hover:bg-[#2A2D34] text-[#838998]"
                        >
                          {metric.expanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      ) : (
                        <div className="w-6" />
                      )}

                      {/* Line Item Name */}
                      <div className="w-1/2">
                        <span className="text-sm text-white">{item.name}</span>
                      </div>

                      {/* Value Input (direct mode) or Total Display (breakdown mode) */}
                      <div className="w-1/2">
                        {isBreakdownMode ? (
                          <div className="text-right">
                            <span className="text-sm font-body text-white">
                              {formatCurrencyDisplay(metric.annual) || '0,00'}
                            </span>
                            <span className="text-xs text-[#838998] ml-1">total</span>
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={formatCurrencyDisplay(metric.annual)}
                            onChange={(e) => handleDirectValueChange(item.id, e.target.value)}
                            onPaste={(e) => {
                              e.preventDefault()
                              const pastedText = e.clipboardData.getData('text')
                              handleDirectValueChange(item.id, pastedText)
                            }}
                            placeholder="0,00"
                            className="w-full h-9 rounded-md border border-[#2A2D34] bg-[#24262B] px-3 py-1 text-sm text-white text-right placeholder:text-[#838998] focus:outline-none focus:ring-2 focus:ring-[#D2FA52] focus:ring-offset-1 focus:ring-offset-[#1E2024] font-body"
                          />
                        )}
                      </div>

                    </div>

                    {/* Breakdown Section (expanded) */}
                    {isBreakdownMode && metric.expanded && (
                      <div className="bg-[#24262B] border-t border-[#2A2D34] p-3">
                        {/* Breakdowns List */}
                        <div className="space-y-2 mb-3">
                          {metric.breakdowns.map((breakdown, idx) => {
                            // Check if client is valid (has a codigo that exists in availableClients)
                            const isValidClient = availableClients.some(
                              (c) => c.codigo === breakdown.dimensionId
                            )
                            return (
                            <div
                              key={idx}
                              className="flex items-center gap-2 pl-6"
                            >
                              {/* Client Input */}
                              <div className="w-1/2">
                                <input
                                  type="text"
                                  list={`clients-${item.id}-${idx}`}
                                  value={breakdown.dimensionLabel}
                                  onChange={(e) => {
                                    const value = e.target.value
                                    const matchingClient = availableClients.find(
                                      (c) => c.nome === value
                                    )
                                    if (matchingClient) {
                                      updateBreakdown(item.id, idx, 'dimensionId', matchingClient.codigo)
                                    } else {
                                      setMetrics((prev) => ({
                                        ...prev,
                                        [item.id]: {
                                          ...prev[item.id],
                                          breakdowns: prev[item.id].breakdowns.map((b, i) =>
                                            i === idx
                                              ? { ...b, dimensionId: '', dimensionLabel: value }
                                              : b
                                          ),
                                        },
                                      }))
                                    }
                                  }}
                                  placeholder="Digite ou selecione um cliente"
                                  className={`w-full h-8 rounded-md border px-2 py-1 text-sm text-white placeholder:text-[#5a5f6b] focus:outline-none focus:ring-2 focus:ring-[#D2FA52] ${
                                    isValidClient
                                      ? 'border-[#2A2D34] bg-[#1E2024]'
                                      : 'border-amber-500/50 bg-amber-500/5'
                                  }`}
                                />
                                {!isValidClient && breakdown.dimensionLabel && (
                                  <p className="text-xs text-amber-400 mt-0.5">Cliente não encontrado</p>
                                )}
                                <datalist id={`clients-${item.id}-${idx}`}>
                                  {availableClients.map((client) => (
                                    <option key={client.codigo} value={client.nome} />
                                  ))}
                                </datalist>
                              </div>

                              {/* Value */}
                              <div className="w-1/2">
                                <input
                                  type="text"
                                  value={formatCurrencyDisplay(breakdown.annual)}
                                  onChange={(e) =>
                                    updateBreakdown(
                                      item.id,
                                      idx,
                                      'annual',
                                      parseCurrencyToNumber(e.target.value)
                                    )
                                  }
                                  placeholder="0,00"
                                  className="w-full h-8 rounded-md border border-[#2A2D34] bg-[#1E2024] px-2 py-1 text-sm text-white text-right placeholder:text-[#5a5f6b] focus:outline-none focus:ring-2 focus:ring-[#D2FA52] font-body"
                                />
                              </div>

                              {/* Remove */}
                              <button
                                type="button"
                                onClick={() => removeBreakdown(item.id, idx)}
                                className="p-1 rounded hover:bg-[#2A2D34] text-[#838998] hover:text-[#F74A4A] transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          )})}

                        </div>

                        {/* Add Breakdown Buttons */}
                        <div className="flex items-center gap-2 ml-6">
                          <button
                            type="button"
                            onClick={() => addBreakdown(item.id)}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#D2FA52] hover:bg-[#D2FA52]/10 rounded-md transition-colors"
                          >
                            <Plus className="h-3 w-3" />
                            Adicionar Cliente
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowClientPasteArea(showClientPasteArea === item.id ? null : item.id)
                              setClientPasteText('')
                              if (showClientPasteArea !== item.id) {
                                setTimeout(() => clientPasteTextareaRef.current?.focus(), 100)
                              }
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#838998] hover:text-white hover:bg-[#2A2D34] rounded-md transition-colors"
                          >
                            <ClipboardPaste className="h-3 w-3" />
                            Colar Clientes
                          </button>
                        </div>

                        {/* Client Paste Area */}
                        {showClientPasteArea === item.id && (
                          <div className="mt-3 ml-6 p-3 rounded-lg bg-[#1E2024] border border-[#2A2D34]">
                            <p className="text-xs text-[#838998] mb-2">
                              Cole os clientes no formato: "NOME DO CLIENTE    VALOR" (um por linha)
                            </p>
                            <textarea
                              ref={clientPasteTextareaRef}
                              value={clientPasteText}
                              onChange={(e) => setClientPasteText(e.target.value)}
                              placeholder={`BB CORRETORA DE SEGU    23.700.000,00
PETROBRAS               48.533.000,00
BANCO DO BRASIL         9.500.000,00
...`}
                              rows={5}
                              className="w-full rounded-md border border-[#2A2D34] bg-[#24262B] px-3 py-2 text-sm text-white placeholder:text-[#5a5f6b] focus:outline-none focus:ring-2 focus:ring-[#D2FA52] focus:ring-offset-1 focus:ring-offset-[#1E2024] font-body resize-none"
                            />
                            <div className="flex items-center justify-end gap-2 mt-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowClientPasteArea(null)
                                  setClientPasteText('')
                                }}
                                className="px-3 py-1.5 text-xs font-medium text-[#838998] hover:text-white transition-colors"
                              >
                                Cancelar
                              </button>
                              <button
                                type="button"
                                onClick={() => handleClientPasteApply(item.id)}
                                disabled={!clientPasteText.trim()}
                                className="px-3 py-1.5 text-xs font-medium bg-[#D2FA52] text-[#0D0E10] rounded-md hover:bg-[#c2ea42] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Aplicar
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Subtotal + Ajuste = Total */}
                        {metric.breakdowns.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-[#2A2D34] space-y-2 pl-6">
                            {/* Subtotal */}
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-[#838998]">Subtotal Clientes:</span>
                              <span className="font-body text-white">
                                {formatCurrencyDisplay(breakdownSum) || '0,00'}
                              </span>
                            </div>

                            {/* Ajuste */}
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-[#838998]">Ajuste:</span>
                              <input
                                type="text"
                                value={formatCurrencyDisplay(metric.ajuste, true)}
                                onChange={(e) => handleAjusteChange(item.id, e.target.value)}
                                placeholder="0,00"
                                className="w-[120px] h-8 rounded-md border border-[#2A2D34] bg-[#1E2024] px-2 py-1 text-sm text-white text-right placeholder:text-[#5a5f6b] focus:outline-none focus:ring-2 focus:ring-[#D2FA52] font-body"
                              />
                            </div>

                            {/* Total */}
                            <div className="flex items-center justify-between text-sm font-medium">
                              <span className="text-[#D2FA52]">Total:</span>
                              <span className="font-body text-[#D2FA52]">
                                {formatCurrencyDisplay(metric.annual) || '0,00'}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="mx-4 mb-4 p-3 rounded-md bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm font-medium text-amber-400 mb-2">
                Corrija os seguintes erros antes de salvar:
              </p>
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((error, idx) => (
                  <li key={idx} className="text-sm text-amber-300">
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Save Error */}
          {saveError && (
            <div className="mx-4 mb-4 p-3 rounded-md bg-red-500/10 border border-red-500/20">
              <p className="text-sm text-red-400">Erro ao salvar: {saveError.message}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-[#2A2D34]">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[#838998] hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium bg-[#D2FA52] text-[#0D0E10] rounded-lg hover:bg-[#c2ea42] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {savingLineItem ? `Salvando ${savingLineItem}...` : 'Salvando...'}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Salvar Metas
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default DREBudgetModal
